import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Search, DollarSign, Calendar, CheckCircle, Clock, FileText as FileInvoice, XCircle, TrendingUp, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { saleService } from '../../services/saleService';
import { notificationService } from '../../services/notificationService';
import { SaleWithDetails } from '../../types/sale';
import ConfirmDialog from '../ui/ConfirmDialog';
import DocumentViewer from '../ui/DocumentViewer';

// Exportar para que sea considerado un módulo
export {};

interface SaleListProps {
  onAdd: () => void;
  onEdit: (sale: SaleWithDetails) => void;
}

const SaleList = ({ onAdd, onEdit }: SaleListProps) => {
  const [sales, setSales] = useState<SaleWithDetails[]>([]);
  const [filteredSales, setFilteredSales] = useState<SaleWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState<SaleWithDetails | null>(null);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{ url: string; name: string } | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const [selectedSale, setSelectedSale] = useState<SaleWithDetails | null>(null);

  const statusOptions = [
    { value: 'all', label: 'Todas', color: 'bg-gray-100 text-gray-800' },
    { value: 'Generada', label: 'Generadas', color: 'bg-blue-100 text-blue-800' },
    { value: 'Facturada', label: 'Facturadas', color: 'bg-orange-100 text-orange-800' },
    { value: 'Vencida', label: 'Vencidas', color: 'bg-red-100 text-red-800' },
    { value: 'Completada', label: 'Completadas', color: 'bg-green-100 text-green-800' }
  ];

  const statusIcons = {
    Generada: <Clock className="w-4 h-4" />,
    Facturada: <FileInvoice className="w-4 h-4" />,
    Vencida: <XCircle className="w-4 h-4" />,
    Completada: <CheckCircle className="w-4 h-4" />
  };

  useEffect(() => {
    loadSales();
  }, [statusFilter]);

  useEffect(() => {
    filterSales();
  }, [sales, searchTerm]);

  const loadSales = async () => {
    setIsLoading(true);
    try {
      // Primero verificar y actualizar ventas vencidas
      try {
        const updatedCount = await saleService.checkAndUpdateOverdueSales();
        if (updatedCount > 0) {
          notificationService.info(
            'Ventas vencidas actualizadas',
            `Se han actualizado ${updatedCount} venta(s) como vencidas automáticamente.`
          );
        }
      } catch (error) {
        console.error('Error al verificar ventas vencidas:', error);
        // No mostrar error al usuario, solo loggearlo para no interrumpir la carga
      }

      let data: SaleWithDetails[] = [];
      if (statusFilter === 'all') {
        data = await saleService.getSales();
      } else {
        const allSales = await saleService.getSales();
        data = allSales.filter(sale => sale.status === statusFilter);
      }
      
      setSales(data);
    } catch (error) {
      console.error('Error al cargar ventas:', error);
      notificationService.error('Error al cargar ventas', 'No se pudieron cargar las ventas. Por favor, recargue la página.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterSales = () => {
    let filtered = [...sales];

    // Filtrar por término de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(sale =>
        sale.sale_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredSales(filtered);
  };

  const handleDeleteSale = async (sale: SaleWithDetails) => {
    setSaleToDelete(sale);
    setShowDeleteDialog(true);
  };

  const confirmDeleteSale = async () => {
    if (!saleToDelete) return;

    try {
      const success = await saleService.deleteSale(saleToDelete.id);
      if (success) {
        notificationService.success('Venta eliminada', 'La venta ha sido eliminada correctamente.');
        loadSales(); // Recargar la lista
      } else {
        notificationService.error('Error al eliminar venta', 'No se pudo eliminar la venta. Por favor, intente nuevamente.');
      }
    } catch (error) {
      console.error('Error al eliminar venta:', error);
      notificationService.error('Error al eliminar venta', 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    } finally {
      setShowDeleteDialog(false);
      setSaleToDelete(null);
    }
  };

  const handleViewDocument = (sale: SaleWithDetails) => {
    if (sale.sale_document_url) {
      setSelectedDocument({
        url: sale.sale_document_url,
        name: sale.sale_document || 'Documento de venta'
      });
      setShowDocumentDialog(true);
    }
  };

  const handleStatusUpdate = async (saleId: number, newStatus: SaleWithDetails['status']) => {
    setUpdatingStatus(saleId);
    try {
      const updatedSale = await saleService.updateSaleStatus(saleId, newStatus);
      if (updatedSale) {
        notificationService.success('Estado actualizado', 'El estado de la venta ha sido actualizado correctamente.');
        loadSales(); // Recargar la lista
      } else {
        notificationService.error('Error al actualizar estado', 'No se pudo actualizar el estado. Por favor, intente nuevamente.');
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      notificationService.error('Error al actualizar estado', 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Evitar el problema de zona horaria parseando manualmente la fecha
    // Esto asegura que la fecha se muestre correctamente sin pérdida de día
    if (dateString && dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      return date.toLocaleDateString('es-PE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    // Fallback para otros formatos de fecha
    return new Date(dateString).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getProfitMarginColor = (margin: number) => {
    if (margin >= 30) return 'text-green-600 font-semibold';
    if (margin >= 15) return 'text-yellow-600 font-medium';
    return 'text-red-600 font-medium';
  };

  const getNextStatus = (currentStatus: string): SaleWithDetails['status'] | null => {
    switch (currentStatus) {
      case 'Generada':
        return 'Facturada';
      case 'Facturada':
        return 'Completada';
      case 'Completada':
        return null;
      case 'Vencida':
        return null;
      default:
        return null;
    }
  };

  const getNextStatusText = (currentStatus: string): string => {
    const nextStatus = getNextStatus(currentStatus);
    if (!nextStatus) return '';
    
    switch (nextStatus) {
      case 'Facturada':
        return 'Marcar como Facturada';
      case 'Completada':
        return 'Marcar como Completada';
      default:
        return '';
    }
  };

  const getStatusColor = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.color : 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  };

  const getDaysUntilDue = (creditDueDate: string | undefined, status: string) => {
    if (!creditDueDate || status !== 'Facturada') {
      return null;
    }

    const today = new Date();
    const dueDate = new Date(creditDueDate + 'T00:00:00');
    
    // Configurar ambas fechas al inicio del día para comparación precisa
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const getDueDateInfo = (creditDueDate: string | undefined, status: string) => {
    const daysUntilDue = getDaysUntilDue(creditDueDate, status);
    
    if (daysUntilDue === null) {
      return null;
    }
    
    if (daysUntilDue > 0) {
      return {
        text: `${daysUntilDue} día${daysUntilDue !== 1 ? 's' : ''} para vencer`,
        color: daysUntilDue <= 7 ? 'text-orange-600 font-semibold' : 'text-blue-600 font-medium',
        icon: <Clock className="w-4 h-4" />
      };
    } else if (daysUntilDue === 0) {
      return {
        text: 'Vence hoy',
        color: 'text-red-600 font-semibold',
        icon: <AlertCircle className="w-4 h-4" />
      };
    } else {
      return {
        text: `${Math.abs(daysUntilDue)} día${Math.abs(daysUntilDue) !== 1 ? 's' : ''} vencida`,
        color: 'text-red-600 font-semibold',
        icon: <XCircle className="w-4 h-4" />
      };
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Ventas</h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Total: {sales.length} ventas</span>
          <Button onClick={onAdd} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Venta
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((status) => (
            <button
              key={status.value}
              onClick={() => setStatusFilter(status.value)}
              className={`px-3 py-2 sm:px-4 rounded-lg font-medium transition-colors text-sm ${
                statusFilter === status.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
        
        {/* Búsqueda */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por número, cliente o producto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Sales List */}
        <div className="lg:col-span-2 space-y-4">
          {filteredSales.length === 0 ? (
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-sm border text-center">
              <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'all' ? 'No se encontraron ventas con los filtros aplicados' : 'No hay ventas registradas'}
              </p>
            </div>
          ) : (
            filteredSales.map((sale) => (
              <div
                key={sale.id}
                className={`bg-white p-4 sm:p-6 rounded-lg shadow-sm border cursor-pointer transition-all hover:shadow-md ${
                  selectedSale?.id === sale.id ? 'border-primary-500 ring-2 ring-primary-200' : ''
                }`}
                onClick={() => setSelectedSale(sale)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 truncate">{sale.sale_number}</h3>
                    <p className="text-sm text-gray-600 truncate">{sale.customer_name}</p>
                    <p className="text-sm text-gray-600 truncate">{sale.product.name}</p>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    <span className={`px-2 py-1 sm:px-3 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(sale.status)}`}>
                      {statusIcons[sale.status as keyof typeof statusIcons] || statusIcons.Generada}
                      <span className="hidden sm:inline">{getStatusLabel(sale.status)}</span>
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 mb-1">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      <span className="hidden sm:inline">
                        {formatDate(sale.sale_date)}
                      </span>
                      <span className="sm:hidden">
                        {new Date(sale.sale_date).toLocaleDateString('es-ES')}
                      </span>
                      <span className="ml-3">
                        {formatCurrency(sale.total_with_igv)}
                      </span>
                      <span className={`ml-3 ${getProfitMarginColor(sale.profit_margin)}`}>
                        <TrendingUp className="w-4 h-4 inline mr-1" />
                        {sale.profit_margin.toFixed(1)}%
                      </span>
                    </div>
                    
                    {/* Mostrar información de vencimiento para ventas facturadas */}
                    {getDueDateInfo(sale.credit_due_date, sale.status) && (
                      <div className={`text-sm flex items-center space-x-1 ${getDueDateInfo(sale.credit_due_date, sale.status)?.color}`}>
                        {getDueDateInfo(sale.credit_due_date, sale.status)?.icon}
                        <span>{getDueDateInfo(sale.credit_due_date, sale.status)?.text}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex space-x-2 ml-4">
                    {/* Botón para cambiar estado */}
                    {getNextStatus(sale.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusUpdate(sale.id, getNextStatus(sale.status)!);
                        }}
                        disabled={updatingStatus === sale.id}
                        className="text-green-600 hover:text-green-800 hover:bg-green-50"
                      >
                        {updatingStatus === sale.id ? (
                          <div className="w-4 h-4 animate-spin rounded-full border-b-2 border-green-600"></div>
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        <span className="sr-only">{getNextStatusText(sale.status)}</span>
                      </Button>
                    )}
                      
                    {/* Ver documento */}
                    {sale.sale_document_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDocument(sale);
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="sr-only">Ver documento</span>
                      </Button>
                    )}
                      
                    {/* Editar */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(sale);
                      }}
                      className="text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                    >
                      <Edit className="w-4 h-4" />
                      <span className="sr-only">Editar</span>
                    </Button>
                      
                    {/* Eliminar */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSale(sale);
                      }}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="sr-only">Eliminar</span>
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Sale Details - Solo visible en desktop */}
        <div className="hidden lg:block">
          {selectedSale ? (
            <div className="bg-white p-6 rounded-lg shadow-sm border max-h-[calc(100vh-12rem)] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalles de Venta</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Número de Venta</label>
                  <p className="text-gray-900">{selectedSale.sale_number}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Cliente</label>
                  <p className="text-gray-900">{selectedSale.customer_name}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Producto</label>
                  <p className="text-gray-900">{selectedSale.product.name}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Cantidad</label>
                  <p className="text-gray-900">{selectedSale.quantity}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Precio Venta Unitario</label>
                  <p className="text-gray-900">{formatCurrency(selectedSale.unit_sale_price)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Precio Costo Unitario</label>
                  <p className="text-gray-900">{formatCurrency(selectedSale.unit_cost_price)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Subtotal</label>
                  <p className="text-gray-900">{formatCurrency(selectedSale.subtotal)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Costo Total</label>
                  <p className="text-gray-900">{formatCurrency(selectedSale.total_cost)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Ganancia Neta</label>
                  <p className={`text-lg font-bold ${getProfitMarginColor(selectedSale.profit_margin)}`}>
                    {formatCurrency(selectedSale.net_profit)}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Margen de Ganancia</label>
                  <p className={`text-lg font-bold ${getProfitMarginColor(selectedSale.profit_margin)}`}>
                    {selectedSale.profit_margin.toFixed(2)}%
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">IGV ({selectedSale.igv_percentage}%)</label>
                  <p className="text-gray-900">{formatCurrency(selectedSale.igv_amount)}</p>
                </div>
                
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">Total con IGV</label>
                    <p className="text-lg font-bold text-primary-600">
                      {formatCurrency(selectedSale.total_with_igv)}
                    </p>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Método de Pago</label>
                  <p className="text-gray-900">{selectedSale.payment_method}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Término de Pago</label>
                  <p className="text-gray-900">{selectedSale.payment_terms}</p>
                </div>
                
                {selectedSale.delivery_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Fecha de Entrega</label>
                    <p className="text-gray-900">{formatDate(selectedSale.delivery_date)}</p>
                  </div>
                )}
                
                {selectedSale.notes && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Notas</label>
                    <p className="text-gray-900">{selectedSale.notes}</p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Fecha de Venta</label>
                  <p className="text-gray-900">{formatDate(selectedSale.sale_date)}</p>
                </div>
                
                {selectedSale.order_number && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Nº Orden de Pedido</label>
                    <p className="text-gray-900">{selectedSale.order_number}</p>
                  </div>
                )}
                
                {selectedSale.order_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Fecha Orden de Pedido</label>
                    <p className="text-gray-900">{formatDate(selectedSale.order_date)}</p>
                  </div>
                )}
                
                {selectedSale.credit_days !== undefined && selectedSale.credit_days > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Días de Crédito</label>
                    <p className="text-gray-900">{selectedSale.credit_days} días</p>
                  </div>
                )}
                
                {selectedSale.credit_due_date && selectedSale.status === 'Facturada' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Fecha de Vencimiento</label>
                    <p className="text-gray-900">{formatDate(selectedSale.credit_due_date)}</p>
                    {getDaysUntilDue(selectedSale.credit_due_date, selectedSale.status) !== null && (
                      <div className={`mt-1 flex items-center space-x-1 ${getDueDateInfo(selectedSale.credit_due_date, selectedSale.status)?.color}`}>
                        {getDueDateInfo(selectedSale.credit_due_date, selectedSale.status)?.icon}
                        <span className="text-sm font-medium">
                          {getDueDateInfo(selectedSale.credit_due_date, selectedSale.status)?.text}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Estado</label>
                  <div className="flex items-center space-x-2 mt-1">
                    {statusIcons[selectedSale.status as keyof typeof statusIcons]}
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedSale.status)}`}>
                      {getStatusLabel(selectedSale.status)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-sm border text-center">
              <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Selecciona una venta para ver los detalles</p>
            </div>
          )}
        </div>
      </div>

      {/* Diálogo de confirmación para eliminar */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={confirmDeleteSale}
        title="Eliminar Venta"
        message={`¿Está seguro de que desea eliminar la venta ${saleToDelete?.sale_number}? Esta acción no se puede deshacer y el stock del producto será devuelto.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Visor de documentos */}
      <DocumentViewer
        isOpen={showDocumentDialog}
        onClose={() => setShowDocumentDialog(false)}
        documentUrl={selectedDocument?.url || ''}
        documentName={selectedDocument?.name || ''}
      />
    </div>
  );
};

export default SaleList;